/**
 * @component MCTestEditor
 * @description QM Modul 4a (#919a v2.405.0): MC-Test-Editor als Tab in ProzessDetailPage.
 *   Tests CRUD + Frage-Pool + Zuweisungen.
 */
import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import {
  Plus, Trash2, Pencil, Check, X, Loader2, Users, Award, ListChecks, Sparkles
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function NumInput({ value, onChange, placeholder, min, max, className = '' }) {
  return (
    <input
      type="number"
      value={value ?? ''}
      onChange={e => {
        const v = e.target.value;
        onChange(v === '' ? null : parseInt(v, 10));
      }}
      min={min}
      max={max}
      placeholder={placeholder}
      className={`px-2 py-1.5 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm text-[var(--text-primary,#1A1A2E)] focus:outline-none focus:border-[#338edc] ${className}`}
    />
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? 'bg-[#338edc]' : 'bg-[var(--border-default,#E5E7EB)]'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`} />
    </button>
  );
}

export default function MCTestEditor({ prozessId, prozess, isAdmin }) {
  const { t } = useLanguage();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTestId, setActiveTestId] = useState(null);
  const [fragen, setFragen] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadTests = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/prozesse/${prozessId}/tests`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setTests(data);
        if (data.length && !activeTestId) setActiveTestId(data[0].id);
      }
    } finally {
      setLoading(false);
    }
  }, [prozessId, activeTestId]);

  const loadFragen = useCallback(async () => {
    if (!activeTestId) { setFragen([]); return; }
    const r = await fetch(`${API}/api/prozesse/tests/${activeTestId}/fragen`, { credentials: 'include' });
    if (r.ok) setFragen(await r.json());
  }, [activeTestId]);

  useEffect(() => { loadTests(); }, [loadTests]);
  useEffect(() => { loadFragen(); }, [loadFragen]);

  const activeTest = tests.find(t => t.id === activeTestId);

  async function createTest() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/prozesse/${prozessId}/tests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ titel: t('qmTest.newTestDefault', 'Neuer Test') }),
      });
      if (r.ok) {
        const t2 = await r.json();
        await loadTests();
        setActiveTestId(t2.id);
      }
    } finally { setSaving(false); }
  }

  async function updateTest(patch) {
    if (!activeTest) return;
    const r = await fetch(`${API}/api/prozesse/tests/${activeTest.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (r.ok) {
      const updated = await r.json();
      setTests(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
    }
  }

  async function deleteTest() {
    if (!activeTest) return;
    if (!confirm(t('qmTest.confirmDeleteTest', 'Test wirklich loeschen?'))) return;
    const r = await fetch(`${API}/api/prozesse/tests/${activeTest.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (r.ok) {
      setActiveTestId(null);
      await loadTests();
    }
  }

  if (loading) {
    return <div className="p-12 text-center text-[var(--text-tertiary,#6B7280)]"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  }

  if (!tests.length) {
    return (
      <div className="text-center py-16">
        <Award className="w-10 h-10 mx-auto mb-3 opacity-40" strokeWidth={1.5} />
        <p className="text-sm text-[var(--text-secondary,#4B5563)] mb-4">
          {t('qmTest.empty', 'Noch kein Test fuer diesen Prozess angelegt')}
        </p>
        {isAdmin && (
          <button
            onClick={createTest}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2778c1] transition disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> {t('qmTest.createTest', 'Test anlegen')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Test-Tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {tests.map(tst => (
          <button
            key={tst.id}
            onClick={() => setActiveTestId(tst.id)}
            className={`px-3 py-1.5 rounded-lg text-sm transition ${
              tst.id === activeTestId
                ? 'bg-[#338edc] text-white'
                : 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] border border-[var(--border-default,#E5E7EB)] hover:border-[#338edc]'
            }`}
          >
            {tst.titel}
            {!tst.aktiv && <span className="ml-2 opacity-60">({t('qmTest.inactive', 'inaktiv')})</span>}
          </button>
        ))}
        {isAdmin && (
          <button
            onClick={createTest}
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-dashed border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-secondary,#4B5563)] hover:border-[#338edc] hover:text-[#338edc] transition disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /> {t('qmTest.newTest', 'Neuer Test')}
          </button>
        )}
      </div>

      {activeTest && (
        <>
          {/* Test-Settings */}
          <TestSettingsCard
            test={activeTest}
            isAdmin={isAdmin}
            onSave={updateTest}
            onDelete={deleteTest}
          />

          {/* Fragen-Pool */}
          <div className="mt-6">
            <FragenPoolEditor
              testId={activeTest.id}
              fragen={fragen}
              isAdmin={isAdmin}
              onChange={loadFragen}
            />
          </div>

          {/* Zuweisungen-Liste (abgeleitet aus dem Prozess-Kreis, read-only) */}
          <ZuweisungenList testId={activeTest.id} />
        </>
      )}
    </div>
  );
}

// ─── Test Settings Card ─────────────────────────────────────

function TestSettingsCard({ test, isAdmin, onSave, onDelete }) {
  const { t } = useLanguage();
  const [titel, setTitel] = useState(test.titel);
  const [editing, setEditing] = useState(false);

  useEffect(() => { setTitel(test.titel); }, [test.id, test.titel]);

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              autoFocus
              value={titel}
              onChange={e => setTitel(e.target.value)}
              onBlur={() => { onSave({ titel }); setEditing(false); }}
              onKeyDown={e => { if (e.key === 'Enter') { onSave({ titel }); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
              className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)] bg-transparent border-b-2 border-[#338edc] outline-none w-full py-0.5"
            />
          ) : (
            <h3
              onClick={isAdmin ? () => setEditing(true) : undefined}
              className={`text-lg font-semibold text-[var(--text-primary,#1A1A2E)] ${isAdmin ? 'cursor-text' : ''}`}
            >
              {test.titel}
            </h3>
          )}
          <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-tertiary,#6B7280)]">
            <span className="flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> {test.fragen_count} {t('qmTest.questions', 'Fragen')}</span>
            <span>·</span>
            <span>{test.versuche_count} {t('qmTest.attempts', 'Versuche')}</span>
            <span>·</span>
            <span>{test.bestanden_count} {t('qmTest.passed', 'bestanden')}</span>
            <span>·</span>
            <span>{test.zuweisungen_count} {t('qmTest.assignments', 'Zuweisungen')}</span>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {/* #1731: Zuweisung läuft zentral über den Prozess-Kreis (Benachrichtigungen-Tab) */}
            <span className="text-xs text-[var(--text-tertiary,#6B7280)]">
              {t('qmTest.assignViaKreis', 'Zuweisung über den Zuweisungskreis (Tab „Benachrichtigungen")')}
            </span>
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-rose-600 transition"
              title={t('qmTest.deleteTest', 'Test loeschen')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">
            {t('qmTest.passThreshold', 'Bestehensgrenze')} (%)
          </label>
          <NumInput
            value={test.bestehensgrenze_pct}
            onChange={v => onSave({ bestehensgrenze_pct: v })}
            min={1}
            max={100}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">
            {t('qmTest.poolSize', 'Fragen aus Pool')}
          </label>
          <NumInput
            value={test.anzahl_fragen_aus_pool}
            onChange={v => onSave({ anzahl_fragen_aus_pool: v })}
            min={1}
            placeholder={t('qmTest.allQuestions', 'alle')}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">
            {t('qmTest.cooldownH', 'Cooldown (h)')}
          </label>
          <NumInput
            value={test.wiederholung_cooldown_h}
            onChange={v => onSave({ wiederholung_cooldown_h: v })}
            min={0}
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-[var(--text-tertiary,#6B7280)] mb-1">
            {t('qmTest.maxAttempts', 'Max. Versuche')}
          </label>
          <NumInput
            value={test.max_versuche}
            onChange={v => onSave({ max_versuche: v })}
            min={1}
            className="w-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <label className="flex items-center gap-2 text-sm">
          <Toggle checked={!!test.zufaellige_reihenfolge} onChange={v => onSave({ zufaellige_reihenfolge: v })} />
          <span className="text-[var(--text-secondary,#4B5563)]">{t('qmTest.shuffleOrder', 'Zufaellige Reihenfolge')}</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Toggle checked={!!test.auto_zuweisung_bei_freigabe} onChange={v => onSave({ auto_zuweisung_bei_freigabe: v })} />
          <span className="text-[var(--text-secondary,#4B5563)]">{t('qmTest.autoAssignOnRelease', 'Auto-Zuweisung bei Freigabe')}</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Toggle checked={!!test.aktiv} onChange={v => onSave({ aktiv: v })} />
          <span className="text-[var(--text-secondary,#4B5563)]">{t('qmTest.testActive', 'Test aktiv')}</span>
        </label>
      </div>
    </div>
  );
}

// ─── Fragen-Pool Editor ─────────────────────────────────────

function FragenPoolEditor({ testId, fragen, isAdmin, onChange }) {
  const { t } = useLanguage();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  if (!fragen.length && !adding) {
    return (
      <div className="bg-[var(--bg-card,#fff)] border border-dashed border-[var(--border-default,#E5E7EB)] rounded-card p-8 text-center">
        <p className="text-sm text-[var(--text-tertiary,#6B7280)] mb-3">
          {t('qmTest.noQuestions', 'Noch keine Fragen im Pool')}
        </p>
        {isAdmin && (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2778c1] transition"
          >
            <Plus className="w-4 h-4" /> {t('qmTest.addQuestion', 'Frage hinzufuegen')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">
          {t('qmTest.questionPool', 'Fragen-Pool')} ({fragen.length})
        </h4>
        {isAdmin && !adding && editingId == null && (
          <button
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-secondary,#4B5563)] hover:border-[#338edc] hover:text-[#338edc] transition"
          >
            <Plus className="w-4 h-4" /> {t('qmTest.addQuestion', 'Frage hinzufuegen')}
          </button>
        )}
      </div>

      <div className="space-y-3">
        {fragen.map((f, idx) => (
          editingId === f.id ? (
            <FrageEditor
              key={f.id}
              testId={testId}
              frage={f}
              onCancel={() => setEditingId(null)}
              onSaved={() => { setEditingId(null); onChange(); }}
            />
          ) : (
            <FrageCard
              key={f.id}
              frage={f}
              index={idx}
              isAdmin={isAdmin}
              onEdit={() => setEditingId(f.id)}
              onDelete={async () => {
                if (!confirm(t('qmTest.confirmDeleteQuestion', 'Frage loeschen?'))) return;
                await fetch(`${API}/api/prozesse/tests/fragen/${f.id}`, { method: 'DELETE', credentials: 'include' });
                onChange();
              }}
            />
          )
        ))}
        {adding && (
          <FrageEditor
            testId={testId}
            frage={null}
            onCancel={() => setAdding(false)}
            onSaved={() => { setAdding(false); onChange(); }}
          />
        )}
      </div>
    </div>
  );
}

function FrageCard({ frage, index, isAdmin, onEdit, onDelete }) {
  const { t } = useLanguage();
  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-[var(--text-tertiary,#6B7280)] mb-1">
            {t('qmTest.question', 'Frage')} {index + 1} · {frage.frage_typ === 'multi_choice' ? t('qmTest.multiChoice', 'Multi-Choice') : t('qmTest.singleChoice', 'Single-Choice')}
          </div>
          <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">{frage.frage_text}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={onEdit} className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-[#338edc] transition">
              <Pencil className="w-4 h-4" />
            </button>
            <button onClick={onDelete} className="p-1.5 rounded-lg text-[var(--text-tertiary,#6B7280)] hover:text-rose-600 transition">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <ul className="space-y-1">
        {(frage.antworten || []).map(a => (
          <li key={a.id} className="flex items-start gap-2 text-sm">
            <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${a.ist_richtig ? 'bg-emerald-500' : 'bg-[var(--border-default,#E5E7EB)]'}`} />
            <span className={a.ist_richtig ? 'text-[var(--text-primary,#1A1A2E)] font-medium' : 'text-[var(--text-secondary,#4B5563)]'}>{a.antwort_text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FrageEditor({ testId, frage, onCancel, onSaved }) {
  const { t } = useLanguage();
  const isNew = !frage;
  const [text, setText] = useState(frage?.frage_text || '');
  const [antworten, setAntworten] = useState(
    frage?.antworten?.length
      ? frage.antworten.map(a => ({ antwort_text: a.antwort_text, ist_richtig: a.ist_richtig }))
      : [
          { antwort_text: '', ist_richtig: true },
          { antwort_text: '', ist_richtig: false },
        ]
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const richtigCount = antworten.filter(a => a.ist_richtig).length;
  const frageTyp = richtigCount > 1 ? 'multi_choice' : 'single_choice';

  function setAntwortText(idx, val) {
    setAntworten(prev => prev.map((a, i) => i === idx ? { ...a, antwort_text: val } : a));
  }
  function setRichtig(idx) {
    if (frageTyp === 'multi_choice' || richtigCount > 1) {
      setAntworten(prev => prev.map((a, i) => i === idx ? { ...a, ist_richtig: !a.ist_richtig } : a));
    } else {
      setAntworten(prev => prev.map((a, i) => ({ ...a, ist_richtig: i === idx })));
    }
  }
  function toggleMulti() {
    if (richtigCount > 1) {
      const firstRichtig = antworten.findIndex(a => a.ist_richtig);
      setAntworten(prev => prev.map((a, i) => ({ ...a, ist_richtig: i === firstRichtig })));
    } else {
      setAntworten(prev => prev.map((a, i) => i === 1 && !a.ist_richtig ? { ...a, ist_richtig: true } : a));
    }
  }
  function addAntwort() {
    if (antworten.length >= 6) return;
    setAntworten(prev => [...prev, { antwort_text: '', ist_richtig: false }]);
  }
  function removeAntwort(idx) {
    if (antworten.length <= 2) return;
    setAntworten(prev => prev.filter((_, i) => i !== idx));
  }

  async function save() {
    setErr(null);
    if (!text.trim()) { setErr(t('qmTest.errEmptyQuestion', 'Frage-Text fehlt')); return; }
    if (!antworten.some(a => a.ist_richtig)) { setErr(t('qmTest.errNoCorrect', 'Mindestens eine richtige Antwort markieren')); return; }
    if (antworten.some(a => !a.antwort_text.trim())) { setErr(t('qmTest.errEmptyAnswer', 'Leere Antwort-Felder vorhanden')); return; }

    setSaving(true);
    try {
      const url = isNew
        ? `${API}/api/prozesse/tests/${testId}/fragen`
        : `${API}/api/prozesse/tests/fragen/${frage.id}`;
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          frage_text: text.trim(),
          frage_typ: frageTyp,
          antworten: antworten.map(a => ({ antwort_text: a.antwort_text.trim(), ist_richtig: a.ist_richtig })),
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.error || 'Fehler');
      } else {
        onSaved();
      }
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-[var(--bg-card,#fff)] border-2 border-[#338edc] rounded-card p-4">
      <textarea
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        rows={2}
        placeholder={t('qmTest.questionPlaceholder', 'Frage-Text...')}
        className="w-full px-3 py-2 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm resize-none focus:outline-none focus:border-[#338edc] text-[var(--text-primary,#1A1A2E)]"
      />
      <div className="flex items-center gap-2 mt-3 mb-3 text-xs">
        <Toggle checked={frageTyp === 'multi_choice'} onChange={toggleMulti} />
        <span className="text-[var(--text-secondary,#4B5563)]">
          {frageTyp === 'multi_choice' ? t('qmTest.multiChoice', 'Multi-Choice') : t('qmTest.singleChoice', 'Single-Choice')}
        </span>
      </div>
      <div className="space-y-2">
        {antworten.map((a, i) => (
          <div key={i} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRichtig(i)}
              className={`shrink-0 w-5 h-5 rounded-${frageTyp === 'multi_choice' ? 'md' : 'full'} border-2 flex items-center justify-center transition ${
                a.ist_richtig ? 'bg-emerald-500 border-emerald-500' : 'border-[var(--border-default,#E5E7EB)]'
              }`}
              title={t('qmTest.markCorrect', 'Als richtig markieren')}
            >
              {a.ist_richtig && <Check className="w-3 h-3 text-white" />}
            </button>
            <input
              value={a.antwort_text}
              onChange={e => setAntwortText(i, e.target.value)}
              placeholder={`${t('qmTest.answer', 'Antwort')} ${i + 1}`}
              className="flex-1 px-3 py-1.5 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-sm focus:outline-none focus:border-[#338edc] text-[var(--text-primary,#1A1A2E)]"
            />
            <button
              type="button"
              onClick={() => removeAntwort(i)}
              disabled={antworten.length <= 2}
              className="p-1 text-[var(--text-tertiary,#6B7280)] hover:text-rose-600 transition disabled:opacity-30"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      {antworten.length < 6 && (
        <button
          type="button"
          onClick={addAntwort}
          className="mt-2 inline-flex items-center gap-1 text-xs text-[#338edc] hover:underline"
        >
          <Plus className="w-3 h-3" /> {t('qmTest.addAnswer', 'Antwort hinzufuegen')}
        </button>
      )}
      {err && <p className="mt-3 text-xs text-rose-600">{err}</p>}
      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-sm text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-inset,#F8F9FC)] transition"
        >
          {t('common.cancel', 'Abbrechen')}
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2778c1] transition disabled:opacity-50"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {t('common.save', 'Speichern')}
        </button>
      </div>
    </div>
  );
}

// ─── Zuweisungen-Liste ─────────────────────────────────────

function ZuweisungenList({ testId }) {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/prozesse/tests/${testId}/zuweisungen`, { credentials: 'include' });
      if (r.ok) setRows(await r.json());
    } finally { setLoading(false); }
  }, [testId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!rows.length) return null;

  const STATUS_COLORS = {
    offen: 'text-slate-600 bg-slate-100',
    tour_offen: 'text-blue-700 bg-blue-50',
    test_offen: 'text-amber-700 bg-amber-50',
    bestanden: 'text-emerald-700 bg-emerald-50',
    nicht_bestanden: 'text-rose-700 bg-rose-50',
    abgelaufen: 'text-slate-500 bg-slate-100',
  };

  return (
    <div className="mt-6">
      <h4 className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)] mb-3 flex items-center gap-2">
        <Users className="w-4 h-4" /> {t('qmTest.assignments', 'Zuweisungen')} ({rows.length})
      </h4>
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-inset,#F8F9FC)] text-xs text-[var(--text-tertiary,#6B7280)]">
            <tr>
              <th className="text-left px-3 py-2 font-medium">{t('qmTest.colMa', 'Mitarbeiter')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('qmTest.colDept', 'Abteilung')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('qmTest.colStatus', 'Status')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('qmTest.colScore', 'Score')}</th>
              <th className="text-left px-3 py-2 font-medium">{t('qmTest.colAssignedAt', 'Zugewiesen')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-default,#E5E7EB)]">
            {rows.map(r => (
              <tr key={r.id}>
                <td className="px-3 py-2 text-[var(--text-primary,#1A1A2E)]">{r.ma_name}</td>
                <td className="px-3 py-2 text-[var(--text-secondary,#4B5563)]">{r.abteilung_name || '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-700'}`}>
                    {t(`qmTest.status.${r.status}`, r.status)}
                  </span>
                </td>
                <td className="px-3 py-2 text-[var(--text-secondary,#4B5563)]">
                  {r.letzter_score != null ? `${Number(r.letzter_score).toFixed(0)} %` : '—'}
                </td>
                <td className="px-3 py-2 text-[var(--text-tertiary,#6B7280)] text-xs">
                  {new Date(r.zugewiesen_am).toLocaleDateString('de-AT')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

